<template>
  <div class="categories-inventory-modern">
    <!-- Modals -->
    <new-category :types="stock_item_types" @created="getStockItemCategories" />
    <view-category
      :category="category"
      @updated="getStockItemCategories"
      @close="category = null"
    />

    <!-- Main Content -->
    <div class="categories-content">
      <!-- Header Section -->
      <div class="content-header">
        <div class="header-left">
          <h2 class="section-title">
            <i class="fas fa-tags section-icon"></i>
            Categorias
          </h2>
          <p class="section-subtitle">
            Organize produtos e medicamentos por categorias
          </p>
        </div>
        <div class="header-actions">
          <v-btn @click="$bvModal.show('new_category')" class="btn-add-modern">
            <i class="fas fa-plus mr-2"></i>
            Adicionar Categoria
          </v-btn>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="filters-section">
        <div class="filters-card">
          <div class="filters-grid">
            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-layer-group filter-icon"></i>
                Tipo
              </label>
              <b-select v-model="stock_item_type_id" class="modern-select">
                <b-select-option value="">TODOS</b-select-option>
                <b-select-option
                  v-for="type in stock_item_types"
                  :key="type.id"
                  :value="type.id"
                >
                  {{ type.name }}
                </b-select-option>
              </b-select>
            </div>
            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-search filter-icon"></i>
                Pesquisar
              </label>
              <b-input
                v-model="search"
                placeholder="Digite o nome da categoria"
                class="modern-input"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Content Grid -->
      <div class="content-grid">
        <!-- Table Section -->
        <div class="table-section">
          <!-- No Results -->
          <div v-show="categories.length == 0" class="no-results">
            <i class="fas fa-search no-results-icon"></i>
            <h3 class="no-results-title">Nenhuma categoria encontrada</h3>
            <p class="no-results-text">
              Tente ajustar os filtros ou adicione uma nova categoria
            </p>
          </div>

          <!-- Data Table -->
          <div v-show="categories.length > 0" class="table-card">
            <v-data-table
              dense
              :headers="headers"
              :items="categories"
              :search="search"
              :footer-props="{
                'items-per-page-options': [50, 75, 100],
                'items-per-page-text': 'Mostrar',
              }"
              class="modern-table"
              :no-data-text="'Nenhuma categoria encontrada'"
              :loading-text="'Carregando categorias...'"
            >
              <template #[`item.name`]="{ item }">
                <div class="category-name-cell">
                  <div class="category-name">{{ item.name }}</div>
                </div>
              </template>

              <template #[`item.type.name`]="{ item }">
                <div class="type-cell">
                  <span
                    class="type-badge"
                    :class="getTypeBadgeClass(item.type ? item.type.name : '')"
                  >
                    <i
                      :class="getTypeIcon(item.type ? item.type.name : '')"
                      class="mr-1"
                    ></i>
                    {{ item.type ? item.type.name : "Sem tipo" }}
                  </span>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="actions-cell">
                  <v-btn small @click="category = item" class="btn-action-edit">
                    <i class="fas fa-edit mr-1"></i>
                    Editar
                  </v-btn>
                  <confirmation-dialog
                    small
                    message-title="EXCLUIR CATEGORIA"
                    message-body="Deseja realmente excluir esta categoria? Esta ação não pode ser desfeita."
                    open-class="btn-action-delete"
                    open-left-icon="fa-trash"
                    open-text="Excluir"
                    @yes="deleteCategory(item)"
                  />
                </div>
              </template>
            </v-data-table>
          </div>
        </div>

        <!-- Info Section -->
        <div class="info-section">
          <div class="info-card">
            <div class="info-header">
              <h3 class="info-title">
                <i class="fas fa-info-circle mr-2"></i>
                Definição de Categorias
              </h3>
            </div>
            <div class="info-content">
              <div class="info-item">
                <h4 class="info-subtitle">O que são categorias?</h4>
                <p class="info-text">
                  As categorias são uma forma de classificar e organizar
                  produtos e medicamentos no estoque conforme o tipo,
                  facilitando a gestão e localização dos itens.
                </p>
              </div>

              <div class="info-item">
                <h4 class="info-subtitle">Exemplos de categorias:</h4>
                <div class="examples-grid">
                  <div class="example-item">
                    <i class="fas fa-utensils example-icon"></i>
                    <span>Alimentos</span>
                  </div>
                  <div class="example-item">
                    <i class="fas fa-trash-alt example-icon"></i>
                    <span>Descartáveis</span>
                  </div>
                  <div class="example-item">
                    <i class="fas fa-spray-can example-icon"></i>
                    <span>Produtos de Limpeza</span>
                  </div>
                  <div class="example-item">
                    <i class="fas fa-soap example-icon"></i>
                    <span>Higiene Pessoal</span>
                  </div>
                  <div class="example-item">
                    <i class="fas fa-hand-holding-heart example-icon"></i>
                    <span>Higiene Íntima</span>
                  </div>
                  <div class="example-item">
                    <i class="fas fa-pills example-icon"></i>
                    <span>Medicamentos</span>
                  </div>
                </div>
              </div>

              <div class="info-item">
                <h4 class="info-subtitle">Benefícios:</h4>
                <ul class="benefits-list">
                  <li>
                    <i class="fas fa-check mr-2"></i>
                    Organização eficiente do estoque
                  </li>
                  <li>
                    <i class="fas fa-check mr-2"></i>
                    Facilita a localização de itens
                  </li>
                  <li>
                    <i class="fas fa-check mr-2"></i>
                    Melhora o controle de inventário
                  </li>
                  <li>
                    <i class="fas fa-check mr-2"></i>
                    Simplifica relatórios e análises
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Messages -->
      <ErrorsMessages :errors="errors" v-if="errors.length > 0" />
    </div>
  </div>
</template>

<script>
import apiGet from "../../../lib/api-get";

import ErrorsMessages from "../../Components/ErrorsMessages.vue";

import NewCategory from "./categories/new-category.vue";

import ViewCategory from "./categories/view-category.vue";

import api from "../../../services/api";

import toast from "../../../lib/ToastNotification";

import ConfirmationDialog from "../../Components/ConfirmationDialog";

export default {
  name: "Categories",

  components: {
    NewCategory,
    ViewCategory,
    ErrorsMessages,
    ConfirmationDialog,
  },

  data() {
    return {
      errors: [],
      loading: false,

      stock_item_types: [],
      stock_item_type_id: "",

      search: "",
      headers: [
        { text: "NOME", value: "name" },
        { text: "TIPO", value: "type.name" },
        { text: "AÇÕES", value: "actions", sortable: false, width: "200px" },
      ],
      stock_item_categories: [],

      category: null,
    };
  },

  computed: {
    categories() {
      let base = this.stock_item_categories;

      if (this.stock_item_type_id)
        base = base.filter(
          (category) => category.stock_item_type_id === this.stock_item_type_id
        );

      return base.map((category) => ({
        ...category,
        type: this.stock_item_types.find(
          (item) => item.id === category.stock_item_type_id
        ),
      }));
    },
  },

  created() {
    this.getStockItemTypes();
    this.getStockItemCategories();
  },

  methods: {
    getTypeBadgeClass(typeName) {
      const name = typeName.toLowerCase();
      if (name.includes("medicamento") || name.includes("medicine"))
        return "type-medicine";
      if (name.includes("produto") || name.includes("product"))
        return "type-product";
      return "type-default";
    },

    getTypeIcon(typeName) {
      const name = typeName.toLowerCase();
      if (name.includes("medicamento") || name.includes("medicine"))
        return "fas fa-pills";
      if (name.includes("produto") || name.includes("product"))
        return "fas fa-box";
      return "fas fa-tag";
    },

    async getStockItemTypes() {
      try {
        this.loading = true;

        const { error, errors, stock_item_types } = await apiGet.get(
          "/rest-home/stock/items/types",
          {}
        );

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.stock_item_types = stock_item_types;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async getStockItemCategories() {
      try {
        this.loading = true;

        const params = { active: this.active };
        const { error, errors, stock_item_categories } = await apiGet.get(
          "/rest-home/stock/items/categories",
          params
        );

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.stock_item_categories = stock_item_categories;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async deleteCategory(category) {
      try {
        this.loading = true;

        const data = { id: category.id };

        const response = await api.delete("/rest-home/stock/items/category", {
          data,
        });

        const { error, errors, message } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];

          toast(message, "success");

          this.getStockItemCategories();

          this.getStockItemTypes();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.categories-inventory-modern {
  background: transparent;
}

/* Content Section */
.categories-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Header Section */
.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 24px;
}

.header-left {
  flex: 1;
}

.section-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
}

.section-icon {
  color: #0f65b0;
  margin-right: 12px;
  font-size: 20px;
}

.section-subtitle {
  color: #64748b;
  margin: 0;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-add-modern {
  background: linear-gradient(135deg, #4e9130 0%, #1e884d 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(78, 145, 48, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-add-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(78, 145, 48, 0.4) !important;
}

/* Filters Section */
.filters-section {
  margin-bottom: 8px;
}

.filters-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.filters-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

.modern-input,
.modern-select {
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  transition: all 0.3s ease !important;
  background: white !important;
}

.modern-input:focus,
.modern-select:focus {
  border-color: #0f65b0 !important;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1) !important;
  outline: none !important;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 32px;
  align-items: start;
}

/* Table Section */
.table-section {
  width: 100%;
}

.no-results {
  background: white;
  border-radius: 16px;
  padding: 60px 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-results-title {
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

/* Table Cell Styling */
.category-name-cell {
  padding: 8px 0;
}

.category-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 14px;
}

.type-cell {
  padding: 8px 0;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.type-medicine {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.type-product {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
  border: 1px solid #60a5fa;
}

.type-default {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #475569;
  border: 1px solid #cbd5e1;
}

.status-cell {
  padding: 8px 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-active {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.status-inactive {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.actions-cell {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-action-edit {
  background: linear-gradient(135deg, #1d75be 0%, #155a8a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 11px !important;
  box-shadow: 0 2px 8px rgba(29, 117, 190, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-action-edit:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(29, 117, 190, 0.4) !important;
}

.btn-action-delete {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 11px !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3) !important;
  transition: all 0.3s ease !important;
  padding: 6px 12px !important;
}

.btn-action-delete:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4) !important;
}

/* Info Section */
.info-section {
  position: sticky;
  top: 24px;
}

.info-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.info-header {
  background: linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%);
  padding: 20px;
  border-bottom: 1px solid #0284c7;
}

.info-title {
  font-size: 16px;
  font-weight: 600;
  color: #0c4a6e;
  margin: 0;
  display: flex;
  align-items: center;
}

.info-title i {
  color: #0284c7;
}

.info-content {
  padding: 24px;
}

.info-item {
  margin-bottom: 24px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-subtitle {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.info-text {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

.examples-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.example-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 12px;
  color: #374151;
}

.example-icon {
  color: #0f65b0;
  font-size: 14px;
  width: 16px;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.benefits-list li {
  display: flex;
  align-items: center;
  padding: 6px 0;
  font-size: 13px;
  color: #374151;
}

.benefits-list li i {
  color: #10b981;
  font-size: 12px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .info-section {
    position: static;
  }
}

@media (max-width: 1024px) {
  .content-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .filters-card {
    padding: 16px;
  }

  .section-title {
    font-size: 20px;
  }

  .actions-cell {
    flex-direction: column;
    gap: 4px;
  }

  .btn-action-edit,
  .btn-action-delete {
    width: 100% !important;
    justify-content: center !important;
  }

  .examples-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .info-content {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 18px;
    flex-direction: column;
    gap: 4px;
  }

  .section-subtitle {
    font-size: 13px;
  }

  .info-header {
    padding: 16px;
  }

  .info-title {
    font-size: 14px;
  }
}

/* Override Vuetify styles */
.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}

/* Enhanced hover effects */
.table-card:hover {
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12) !important;
  transition: box-shadow 0.3s ease !important;
}

.info-card:hover {
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12) !important;
  transition: box-shadow 0.3s ease !important;
}
</style>